import { unwrapResult } from "@reduxjs/toolkit";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import AddTaskIcon from "@mui/icons-material/AddTask";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import { Alert, Box, Button, Chip, IconButton, Paper, Stack, Tooltip } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import AddLinkIcon from "@mui/icons-material/AddLink";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import React, { useEffect, useMemo, useState } from "react";
import { APIError, AppId } from "../../../../app/appTypes";
import { LpField } from "../../../../app/components/LpField";
import { useAppDispatch, useDataGridCustomLocaleText } from "../../../../app/hooks";
import { ObjectValidationStatusChip } from "../../../computerizedSystems/components/ObjectValidationStatusChip";
import ValidationProcessWizard from "../../validationProcess/ValidationProcessWizard";
import { CreateAndLinkValidationProjectDeviceDialog, CreateAndLinkValidationProjectDeviceDialogProps } from "../dialogs/CreateAndLinkValidationProjectDeviceDialog";
import { LinkValidationProjectDeviceDialog, LinkValidationProjectDeviceDialogProps } from "../dialogs/LinkValidationProjectDeviceDialog";
import { loadValidationProjectDevices, unlinkValidationProjectDevice, ValidationProjectDeviceDto } from "../validationProjectSlice";
import { useEntityNavigation } from "../../../../app/components/EntityBreadcrumbs";
import { QuickSearchToolbar } from "../../../../app/QuickSearchToolbar";

type DialogState =
    { tag: "none" }
    | { tag: "linkDevice", props: LinkValidationProjectDeviceDialogProps }
    | { tag: "createAndLinkDevice", props: CreateAndLinkValidationProjectDeviceDialogProps };

interface ProjectDevicesTabProps {
    projectId: AppId;
}

export const ProjectDevicesTab = ({ projectId }: ProjectDevicesTabProps) => {
    const dispatch = useAppDispatch();
    const navigateToEntity = useEntityNavigation({ type: "project", id: projectId });
    const [devices, setDevices] = useState<ValidationProjectDeviceDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<APIError | null>(null);
    const [dialog, setDialog] = useState<DialogState>({ tag: "none" });
    const [unlinkingDeviceId, setUnlinkingDeviceId] = useState<AppId | null>(null);
    const [wizardDevice, setWizardDevice] = useState<ValidationProjectDeviceDto | null>(null);
    const localeText = useDataGridCustomLocaleText();

    useEffect(() => {
        setLoading(true);
        dispatch(loadValidationProjectDevices(projectId))
            .then(unwrapResult)
            .then((result) => {
                setDevices(result);
                setError(null);
            })
            .catch((err) => setError(err))
            .finally(() => setLoading(false));
    }, [dispatch, projectId]);

    const handleOpenValidationProcessClick = (processId: AppId) => {
        navigateToEntity({ type: "process", id: processId });
    };

    const handleUnlinkDeviceClick = (device: ValidationProjectDeviceDto) => {
        setUnlinkingDeviceId(String(device.id));
        dispatch(unlinkValidationProjectDevice({ projectId, deviceId: String(device.deviceId) }))
            .then(unwrapResult)
            .then(() => {
                setDevices((current) => current.filter((item) => item.deviceId !== device.deviceId));
                setError(null);
            })
            .catch((err) => setError(err))
            .finally(() => setUnlinkingDeviceId(null));
    };

    const columns = useMemo<GridColDef[]>(() => [
        {
            field: "order",
            headerName: "Lp",
            width: 80,
            sortable: false,
            filterable: false,
            align: "center",
            headerAlign: "center",
            disableColumnMenu: true,
            renderCell: (params) => (
                <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <LpField value={params.value ?? ""} />
                </Box>
            ),
        },
        { field: "name", headerName: "Name", flex: 1, minWidth: 220 },
        { field: "serialNo", headerName: "Serial No", width: 180 },
        {
            field: "status",
            headerName: "Status",
            width: 180,
            renderCell: (params) => <ObjectValidationStatusChip status={params.value} size="small" />,
        },
        {
            field: "validationProcess",
            headerName: "Czy trwa proces walidacyjny",
            width: 220,
            sortable: false,
            filterable: false,
            align: "center",
            headerAlign: "center",
            disableColumnMenu: true,
            renderCell: (params) => {
                return params.row.isValidationProcessInProgress
                    ? <Chip size="small" color="success" label="Tak" />
                    : <Chip size="small" variant="outlined" label="Nie" />;
            },
        },
        {
            field: "actions",
            headerName: "Akcje",
            width: 170,
            sortable: false,
            filterable: false,
            align: "center",
            headerAlign: "center",
            disableColumnMenu: true,
            renderCell: (params) => (
                <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                        <Tooltip title="Przejdź do urządzenia">
                            <IconButton
                                size="small"
                                onClick={() => navigateToEntity({ type: "device", id: String(params.row.deviceId) })}
                                sx={{
                                    color: "text.secondary",
                                    "&:hover": {
                                        color: "info.main",
                                        bgcolor: "info.lighter",
                                    },
                                }}>
                                <PrecisionManufacturingIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        {params.row.validationProcess != null ? (
                            <Tooltip title="Przejdź do procesu walidacyjnego">
                                <IconButton
                                    size="small"
                                    sx={{
                                        color: "primary.main",
                                        "&:hover": {
                                            color: "success.main",
                                            bgcolor: "success.lighter",
                                        },
                                    }}
                                    onClick={() => handleOpenValidationProcessClick(String(params.row.validationProcess.id))}>
                                    <FactCheckIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <Tooltip title="Utwórz proces walidacyjny">
                                <span>
                                    <IconButton
                                        size="small"
                                        sx={{
                                            color: "primary.main",
                                            "&:hover": {
                                                color: "warning.main",
                                                bgcolor: "warning.lighter",
                                            },
                                        }}
                                        onClick={() => setWizardDevice(params.row as ValidationProjectDeviceDto)}>
                                        <AddTaskIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                        <Tooltip title="Odepnij urządzenie z projektu">
                            <span>
                                <IconButton
                                    size="small"
                                    sx={{
                                        color: "error.main",
                                        "&:hover": {
                                            color: "error.dark",
                                            bgcolor: "error.lighter",
                                        },
                                    }}
                                    disabled={unlinkingDeviceId === params.row.id}
                                    onClick={() => handleUnlinkDeviceClick(params.row as ValidationProjectDeviceDto)}>
                                    <LinkOffIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </Box>
            ),
        },
    ], [navigateToEntity, unlinkingDeviceId]);

    return <Paper variant="outlined" sx={{ p: 2 }}>
        {error && <Alert severity="error">{error.kind === "http" ? error.problem?.title ?? "Request failed" : "Request failed"}</Alert>}
        <DataGrid
            loading={loading}
            rows={devices}
            slots={{ toolbar: QuickSearchToolbar }}
            columns={columns}
            localeText={localeText}
            sx={{ minHeight: 320, height: "calc(100vh - 475px)" }}
        />
        <Button size="small" variant="contained" color="primary" startIcon={<AddCircleOutlineIcon />} sx={{ mt: 2 }} onClick={() => setDialog({
            tag: "createAndLinkDevice",
            props: {
                projectId,
                onSuccess: (device) => {
                    setDevices((current) => {
                        const index = current.findIndex((item) => item.id === device.id);
                        if (index >= 0) {
                            const next = [...current];
                            next[index] = device;
                            return next;
                        }
                        return [...current, device];
                    });
                    setDialog({ tag: "none" });
                },
                onCancel: () => setDialog({ tag: "none" }),
            },
        })}>
            Zarejestruj urządzenie
        </Button>
        <Button size="small" variant="outlined" color="primary" startIcon={<AddLinkIcon />} sx={{ mt: 2, ml: 1 }} onClick={() => setDialog({
            tag: "linkDevice",
            props: {
                projectId,
                onSave: (device) => {
                    setDevices((current) => {
                        const index = current.findIndex((item) => item.id === device.id);
                        if (index >= 0) {
                            const next = [...current];
                            next[index] = device;
                            return next;
                        }
                        return [...current, device];
                    });
                    setDialog({ tag: "none" });
                },
                onCancel: () => setDialog({ tag: "none" }),
            },
        })}>
            Podlinkuj urządzenie
        </Button>
        {wizardDevice && <ValidationProcessWizard
            initialDeviceId={String(wizardDevice.deviceId)}
            projectId={projectId}
            onClose={() => setWizardDevice(null)}
            onSave={(process) => {
                setDevices((current) => current.map((device) => device.id === wizardDevice.id ? {
                    ...device,
                    isValidationProcessInProgress: true,
                    validationProcess: {
                        id: Number(process.id),
                        no: process.no,
                    },
                } : device));
                setWizardDevice(null);
                navigateToEntity({ type: "process", id: String(process.id) });
            }}
        />}
        {dialog.tag === "linkDevice" && <LinkValidationProjectDeviceDialog {...dialog.props} />}
        {dialog.tag === "createAndLinkDevice" && <CreateAndLinkValidationProjectDeviceDialog {...dialog.props} />}
    </Paper>;
};
